import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Image, Paperclip, Variable, Upload, Info } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { Audience } from '@/services/PagesServices/MarketingAndNotificationService';
import './ComposeNotification.css';

const ComposeNotification: React.FC = () => {
  const { audiences: storeAudiences } = useMarketingAndNotificationStore();

  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync store data to local state
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences);
    }
  }, [storeAudiences]);

  const handleAudienceToggle = (id: string) => {
    setAudiences(audiences.map(aud =>
      aud.id === id ? { ...aud, checked: !aud.checked } : aud
    ));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const selectedCount = audiences.filter(a => a.checked).length;

  return (
    <div className="trainer-marketing-compose-content">
      <h2 className="trainer-marketing-section-title">Compose Notification</h2>
      <p className="trainer-marketing-section-subtitle">Create and send notifications to your users</p>

      <div className="trainer-marketing-compose-layout">
        <div className="left-panel">
          <div className="trainer-marketing-compose-card">
            <h3 className="trainer-marketing-card-title">Message Details</h3>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className="trainer-marketing-form-input"
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
              />
            </div>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Message</label>
              <textarea
                className="trainer-marketing-form-textarea"
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="trainer-marketing-toolbar">
              <button className="trainer-marketing-toolbar-btn" title="Add Image">
                <Image size={16} />
                <span>Image</span>
              </button>
              <button className="trainer-marketing-toolbar-btn" title="Attach File">
                <Paperclip size={16} />
                <span>Attach</span>
              </button>
              <button className="trainer-marketing-toolbar-btn" title="Add Variable">
                <Variable size={16} />
                <span>Variable</span>
              </button>
            </div>
          </div>

          <div className="trainer-marketing-compose-card">
            <h3 className="trainer-marketing-card-title">Delivery Settings</h3>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Notification Type</label>
              <div className="trainer-marketing-notification-types">
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'email' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'in-app' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`trainer-marketing-type-btn ${notificationType === 'both' ? 'trainer-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="trainer-marketing-form-group">
              <label className="trainer-marketing-form-label">Select Audience</label>
              <div className="trainer-marketing-audience-list">
                {audiences.map(audience => (
                  <label key={audience.id} className="trainer-marketing-audience-item">
                    <input
                      type="checkbox"
                      checked={audience.checked}
                      onChange={() => handleAudienceToggle(audience.id)}
                      className="trainer-marketing-audience-checkbox"
                    />
                    <div className="trainer-marketing-audience-info">
                      <span className="trainer-marketing-audience-name">{audience.name}</span>
                      <span className="trainer-marketing-audience-count">{audience.userCount} users</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="trainer-marketing-form-group">
              <div
                className="trainer-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="trainer-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                <p className="trainer-marketing-upload-text">
                  {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
                </p>
                <p className="trainer-marketing-upload-subtext">
                  or click to browse from your computer
                </p>
                <button className="trainer-marketing-upload-btn" onClick={(e) => e.stopPropagation()}>
                  Select File
                </button>
                <p className="trainer-marketing-upload-hint">Supports .csv files up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="trainer-marketing-form-row">
              <div className="trainer-marketing-form-group trainer-marketing-form-group-half">
                <label className="trainer-marketing-form-label">Schedule</label>
                <select
                  className="trainer-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="trainer-marketing-form-group trainer-marketing-form-group-half">
                <label className="trainer-marketing-form-label">Date & Time (Optional)</label>
                <input
                  type="date"
                  className="trainer-marketing-form-input"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  disabled={scheduleType === 'now'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="trainer-marketing-preview-card">
            <div className="trainer-marketing-preview-header">
              <Mail size={16} className="trainer-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="trainer-marketing-preview-content">
              <div className="trainer-marketing-preview-from">
                <span className="trainer-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="trainer-marketing-preview-to">
                <span className="trainer-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="trainer-marketing-preview-message">
                <h3 className="trainer-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="trainer-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="trainer-marketing-ready-banner">
              <Info size={16} />
              <div className="trainer-marketing-ready-content">
                <span className="trainer-marketing-ready-title">Ready to Send</span>
                <ul className="trainer-marketing-ready-list">
                  <li>• {selectedCount} audience{selectedCount !== 1 ? 's' : ''} selected</li>
                  <li>• Type: {notificationType}</li>
                  <li>• Send immediately</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeNotification;
